<!--
  @component ImportCatalogTab
  @description Catalog search tab for importing bibliographic records
-->
<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { t } from '@/i18n';
  import {
    sudocService,
    type SudocRecord,
    type SudocSearchIndex,
    type SudocDocumentType,
    harvardLibraryService,
    type HarvardLibraryRecord,
    bnfService,
    type BnfRecord,
    type BnfSearchIndex,
    type BnfDocumentType,
    swisscoveryService,
    type SwisscoveryRecord,
    type SwisscoverySearchIndex,
    openLibraryService,
    type OpenLibraryRecord,
    type OpenLibrarySearchIndex,
  } from '@services/catalogs';
  import { notify } from '@services/notificationService';
  import ImportRecordList from './ImportRecordList.svelte';
  import type { UnifiedRecord } from '@types';

  export let selectedRecords: Set<string> = new Set();

  const dispatch = createEventDispatcher<{
    selectionChange: { selectedRecords: Set<string> };
  }>();

  // Catalog source types
  type CatalogSource =
    | 'sudoc'
    | 'bnf'
    | 'swisscovery'
    | 'openLibrary'
    | 'harvardLibrary';

  // Search options for Sudoc
  const searchIndexOptions: { value: SudocSearchIndex; label: string }[] = [
    { value: 'tou', label: 'Tous les mots' },
    { value: 'mti', label: 'Titre' },
    { value: 'aut', label: 'Auteur' },
    { value: 'isb', label: 'ISBN' },
    { value: 'isn', label: 'ISSN' },
    { value: 'ppn', label: 'PPN' },
    { value: 'msu', label: 'Sujet' },
    { value: 'edi', label: 'Editeur' },
    { value: 'nnt', label: 'N° national de thèse' },
  ];

  const documentTypeOptions: { value: SudocDocumentType | ''; label: string }[] = [
    { value: '', label: 'Tous types' },
    { value: 'b', label: 'Livres' },
    { value: 'y', label: 'Thèses' },
    { value: 't', label: 'Périodiques' },
    { value: 'a', label: 'Articles' },
    { value: 'o', label: 'E-books' },
    { value: 'f', label: 'Manuscrits' },
    { value: 'k', label: 'Cartes' },
    { value: 'v', label: 'Vidéos' },
  ];

  // Search options for BnF
  const bnfSearchIndexOptions: { value: BnfSearchIndex; label: string }[] = [
    { value: 'bib.anywhere', label: 'Tous les mots' },
    { value: 'bib.title', label: 'Titre' },
    { value: 'bib.author', label: 'Auteur' },
    { value: 'bib.isbn', label: 'ISBN' },
    { value: 'bib.issn', label: 'ISSN' },
    { value: 'bib.subject', label: 'Sujet' },
    { value: 'bib.publisher', label: 'Éditeur' },
    { value: 'bib.publicationdate', label: 'Année' },
  ];

  const bnfDocumentTypeOptions: { value: BnfDocumentType | ''; label: string }[] = [
    { value: '', label: 'Tous types' },
    { value: 'a', label: 'Textes imprimés' },
    { value: 'c', label: 'Musique notée' },
    { value: 'e', label: 'Cartes' },
    { value: 'g', label: 'Enregistrements sonores' },
    { value: 'h', label: 'Vidéos' },
    { value: 'i', label: 'Images' },
    { value: 's', label: 'Documents électroniques' },
  ];

  // Search options for Swisscovery
  const swisscoverySearchIndexOptions: { value: SwisscoverySearchIndex; label: string }[] = [
    { value: 'alma.all_for_ui', label: 'Tous les mots' },
    { value: 'alma.title', label: 'Titre' },
    { value: 'alma.author', label: 'Auteur' },
    { value: 'alma.isbn', label: 'ISBN' },
    { value: 'alma.issn', label: 'ISSN' },
    { value: 'alma.subject', label: 'Sujet' },
    { value: 'alma.publisher', label: 'Éditeur' },
    { value: 'alma.date', label: 'Année' },
  ];

  // Search options for Open Library
  const openLibrarySearchIndexOptions: { value: OpenLibrarySearchIndex; label: string }[] = [
    { value: 'q', label: 'Tous les mots' },
    { value: 'title', label: 'Titre' },
    { value: 'author', label: 'Auteur' },
    { value: 'isbn', label: 'ISBN' },
    { value: 'subject', label: 'Sujet' },
    { value: 'publisher', label: 'Éditeur' },
  ];

  // Search options for Harvard Library
  type HarvardLibrarySearchIndex = 'keyword' | 'title' | 'author' | 'isbn';
  const harvardLibrarySearchIndexOptions: { value: HarvardLibrarySearchIndex; label: string }[] = [
    { value: 'keyword', label: 'Tous les mots' },
    { value: 'title', label: 'Titre' },
    { value: 'author', label: 'Auteur' },
    { value: 'isbn', label: 'ISBN' },
  ];

  // State
  let selectedCatalogSource: CatalogSource = 'sudoc';
  let isSearching = false;
  let searchResults: UnifiedRecord[] = [];
  let totalResults = 0;
  let currentPage = 1;
  const pageSize = 10;
  let hasSearched = false;

  // Sudoc state
  let searchQuery = '';
  let selectedIndex: SudocSearchIndex = 'tou';
  let selectedDocType: SudocDocumentType | '' = '';

  // BnF state
  let bnfSelectedIndex: BnfSearchIndex = 'bib.anywhere';
  let bnfDocType: BnfDocumentType | '' = '';

  // Swisscovery state
  let swisscoverySelectedIndex: SwisscoverySearchIndex = 'alma.all_for_ui';

  // Open Library state
  let openLibrarySelectedIndex: OpenLibrarySearchIndex = 'q';

  // Harvard Library state
  let harvardLibrarySelectedIndex: HarvardLibrarySearchIndex = 'keyword';

  $: totalPages = Math.ceil(totalResults / pageSize);

  // Reactive current query for button state
  $: currentQuery = searchQuery;

  // Sync selection changes back to parent
  $: dispatch('selectionChange', { selectedRecords });

  // Convert records to unified format
  function sudocToUnified(records: SudocRecord[]): UnifiedRecord[] {
    return records.map((r) => ({
      id: r.ppn,
      item: r.item,
      source: 'sudoc' as const,
      identifiers: {
        ppn: r.ppn,
        isbn: r.item.ISBN,
        issn: r.item.ISSN,
      },
    }));
  }

  function harvardLibraryToUnified(records: HarvardLibraryRecord[]): UnifiedRecord[] {
    return records.map((r) => ({
      id: r.id,
      item: r.item,
      source: 'harvardLibrary' as const,
      thumbnail: r.thumbnail,
      identifiers: {
        isbn: r.item.ISBN,
        issn: r.item.ISSN,
      },
    }));
  }

  function bnfToUnified(records: BnfRecord[]): UnifiedRecord[] {
    return records.map((r) => ({
      id: r.ark,
      item: r.item,
      source: 'bnf' as const,
      identifiers: {
        isbn: r.item.ISBN,
        issn: r.item.ISSN,
      },
    }));
  }

  function swisscoveryToUnified(records: SwisscoveryRecord[]): UnifiedRecord[] {
    return records.map((r) => ({
      id: r.mmsId,
      item: r.item,
      source: 'swisscovery' as const,
      identifiers: {
        isbn: r.item.ISBN,
        issn: r.item.ISSN,
      },
    }));
  }

  function openLibraryToUnified(records: OpenLibraryRecord[]): UnifiedRecord[] {
    return records.map((r) => ({
      id: r.olid,
      item: r.item,
      source: 'openLibrary' as const,
      thumbnail: r.coverUrl,
      identifiers: {
        isbn: r.item.ISBN,
      },
    }));
  }

  // Get current query
  function getCurrentQuery(): string {
    return searchQuery;
  }

  // Search function
  async function handleSearch() {
    const query = getCurrentQuery();
    if (!query.trim()) {
      notify.warning($t('import.enterSearchTerm'));
      return;
    }

    isSearching = true;
    hasSearched = true;
    selectedRecords = new Set();

    try {
      if (selectedCatalogSource === 'sudoc') {
        const result = await sudocService.search(searchQuery, {
          index: selectedIndex,
          documentType: selectedDocType || undefined,
          startRecord: (currentPage - 1) * pageSize + 1,
          maximumRecords: pageSize,
        });
        searchResults = sudocToUnified(result.records);
        totalResults = result.totalResults;
      } else if (selectedCatalogSource === 'bnf') {
        const result = await bnfService.search(searchQuery, {
          index: bnfSelectedIndex,
          documentType: bnfDocType || undefined,
          startRecord: (currentPage - 1) * pageSize + 1,
          maximumRecords: pageSize,
        });
        searchResults = bnfToUnified(result.records);
        totalResults = result.totalResults;
      } else if (selectedCatalogSource === 'swisscovery') {
        const result = await swisscoveryService.search(searchQuery, {
          index: swisscoverySelectedIndex,
          startRecord: (currentPage - 1) * pageSize + 1,
          maximumRecords: pageSize,
        });
        searchResults = swisscoveryToUnified(result.records);
        totalResults = result.totalResults;
      } else if (selectedCatalogSource === 'openLibrary') {
        const result = await openLibraryService.search(searchQuery, {
          index: openLibrarySelectedIndex,
          page: currentPage,
          limit: pageSize,
        });
        searchResults = openLibraryToUnified(result.records);
        totalResults = result.totalResults;
      } else if (selectedCatalogSource === 'harvardLibrary') {
        const result = await harvardLibraryService.search(searchQuery, {
          startIndex: (currentPage - 1) * pageSize,
          maxResults: pageSize,
          searchType: harvardLibrarySelectedIndex,
        });
        searchResults = harvardLibraryToUnified(result.records);
        totalResults = result.totalResults;
      }

      if (searchResults.length === 0) {
        notify.info($t('import.noResults'));
      }
    } catch {
      notify.error($t('import.searchFailed'));
      searchResults = [];
      totalResults = 0;
    } finally {
      isSearching = false;
    }
  }

  function handleCatalogSourceChange(source: CatalogSource) {
    if (source !== selectedCatalogSource) {
      selectedCatalogSource = source;
      searchResults = [];
      totalResults = 0;
      currentPage = 1;
      selectedRecords = new Set();
      hasSearched = false;
    }
  }

  async function goToPage(page: number) {
    if (page < 1 || page > totalPages) return;
    currentPage = page;
    await handleSearch();
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !isSearching) {
      handleSearch();
    }
  }

  // Expose search results for parent
  export function getSearchResults(): UnifiedRecord[] {
    return searchResults;
  }
</script>

<div class="tab-content">
  <!-- Source Sub-tabs -->
  <div class="source-tabs">
    <button
      class="source-tab"
      class:active={selectedCatalogSource === 'sudoc'}
      on:click={() => handleCatalogSourceChange('sudoc')}
    >
      {$t('import.sourceSudoc')}
    </button>
    <button
      class="source-tab"
      class:active={selectedCatalogSource === 'bnf'}
      on:click={() => handleCatalogSourceChange('bnf')}
    >
      {$t('import.sourceBnf')}
    </button>
    <button
      class="source-tab"
      class:active={selectedCatalogSource === 'swisscovery'}
      on:click={() => handleCatalogSourceChange('swisscovery')}
    >
      {$t('import.sourceSwisscovery')}
    </button>
    <button
      class="source-tab"
      class:active={selectedCatalogSource === 'harvardLibrary'}
      on:click={() => handleCatalogSourceChange('harvardLibrary')}
    >
      {$t('import.sourceHarvardLibrary')}
    </button>
    <button
      class="source-tab"
      class:active={selectedCatalogSource === 'openLibrary'}
      on:click={() => handleCatalogSourceChange('openLibrary')}
    >
      {$t('import.sourceOpenLibrary')}
    </button>
  </div>

  <!-- Search Form -->
  <div class="search-form">
    <div class="search-row">
      {#if selectedCatalogSource === 'sudoc'}
        <select
          class="search-index"
          bind:value={selectedIndex}
          aria-label={$t('import.searchIndex')}
        >
          {#each searchIndexOptions as option}
            <option value={option.value}>{option.label}</option>
          {/each}
        </select>
      {:else if selectedCatalogSource === 'bnf'}
        <select
          class="search-index"
          bind:value={bnfSelectedIndex}
          aria-label={$t('import.searchIndex')}
        >
          {#each bnfSearchIndexOptions as option}
            <option value={option.value}>{option.label}</option>
          {/each}
        </select>
      {:else if selectedCatalogSource === 'swisscovery'}
        <select
          class="search-index"
          bind:value={swisscoverySelectedIndex}
          aria-label={$t('import.searchIndex')}
        >
          {#each swisscoverySearchIndexOptions as option}
            <option value={option.value}>{option.label}</option>
          {/each}
        </select>
      {:else if selectedCatalogSource === 'openLibrary'}
        <select
          class="search-index"
          bind:value={openLibrarySelectedIndex}
          aria-label={$t('import.searchIndex')}
        >
          {#each openLibrarySearchIndexOptions as option}
            <option value={option.value}>{option.label}</option>
          {/each}
        </select>
      {:else if selectedCatalogSource === 'harvardLibrary'}
        <select
          class="search-index"
          bind:value={harvardLibrarySelectedIndex}
          aria-label={$t('import.searchIndex')}
        >
          {#each harvardLibrarySearchIndexOptions as option}
            <option value={option.value}>{option.label}</option>
          {/each}
        </select>
      {/if}

      <input
        type="text"
        class="search-input"
        bind:value={searchQuery}
        placeholder={selectedCatalogSource === 'sudoc'
          ? $t('import.sudocSearchHint')
          : selectedCatalogSource === 'bnf'
            ? $t('import.bnfSearchHint')
            : selectedCatalogSource === 'swisscovery'
              ? $t('import.swisscoverySearchHint')
              : selectedCatalogSource === 'openLibrary'
                ? $t('import.openLibrarySearchHint')
                : $t('import.harvardLibrarySearchHint')}
        disabled={isSearching}
        on:keydown={handleKeydown}
      />

      <button
        class="btn btn-primary search-button"
        on:click={handleSearch}
        disabled={isSearching || !currentQuery.trim()}
      >
        {#if isSearching}
          {$t('import.searching')}
        {:else}
          {$t('common.search')}
        {/if}
      </button>
    </div>

    {#if selectedCatalogSource === 'sudoc'}
      <div class="filter-row">
        <label class="filter-label">
          {$t('import.documentType')}:
          <select bind:value={selectedDocType}>
            {#each documentTypeOptions as option}
              <option value={option.value}>{option.label}</option>
            {/each}
          </select>
        </label>
      </div>
    {:else if selectedCatalogSource === 'bnf'}
      <div class="filter-row">
        <label class="filter-label">
          {$t('import.documentType')}:
          <select bind:value={bnfDocType}>
            {#each bnfDocumentTypeOptions as option}
              <option value={option.value}>{option.label}</option>
            {/each}
          </select>
        </label>
      </div>
    {/if}
  </div>

  <!-- Results -->
  <div class="results-container">
    {#if isSearching}
      <div class="loading-state">
        <span class="spinner"></span>
        <span>{$t('import.searching')}</span>
      </div>
    {:else if hasSearched && searchResults.length === 0}
      <div class="empty-state">
        <p>{$t('import.noResults')}</p>
        <p class="hint">{$t('import.tryDifferentSearch')}</p>
      </div>
    {:else if searchResults.length > 0}
      <!-- Results header with total count -->
      <div class="results-info">
        <span class="results-count">{$t('import.resultsCount', { count: totalResults })}</span>
      </div>

      <ImportRecordList records={searchResults} bind:selectedRecords showThumbnails={true} />

      <!-- Pagination -->
      {#if totalPages > 1}
        <div class="pagination">
          <button
            class="btn btn-small"
            disabled={currentPage === 1}
            on:click={() => goToPage(currentPage - 1)}
          >
            {$t('pagination.previous')}
          </button>
          <span class="page-info">
            {$t('pagination.page')}
            {currentPage}
            {$t('pagination.of')}
            {totalPages}
          </span>
          <button
            class="btn btn-small"
            disabled={currentPage === totalPages}
            on:click={() => goToPage(currentPage + 1)}
          >
            {$t('pagination.next')}
          </button>
        </div>
      {/if}
    {:else}
      <div class="empty-state">
        <p>{$t('import.welcomeMessage')}</p>
        <p class="hint">{$t('import.searchHint')}</p>
      </div>
    {/if}
  </div>
</div>

<style>
  .tab-content {
    min-height: 350px;
  }

  /* Source Sub-tabs */
  .source-tabs {
    display: flex;
    gap: 8px;
    margin-bottom: 16px;
  }

  .source-tab {
    padding: 8px 16px;
    background: var(--color-bg-tertiary, #4a4a4a);
    border: 1px solid var(--color-border, #555);
    border-radius: 20px;
    color: var(--color-text-secondary, #aaa);
    font-size: 13px;
    cursor: pointer;
    transition: all 0.2s;
  }

  .source-tab:hover {
    background: var(--color-bg-hover, #555);
    color: var(--color-text-primary, #e8e8e8);
  }

  .source-tab.active {
    background: var(--color-accent, #5c7cfa);
    border-color: var(--color-accent, #5c7cfa);
    color: white;
  }

  /* Search Form */
  .search-form {
    margin-bottom: 20px;
  }

  .search-row {
    display: flex;
    gap: 8px;
    margin-bottom: 12px;
  }

  .search-index {
    width: 180px;
    padding: 10px 12px;
    background: var(--color-bg-primary, #2b2b2b);
    border: 1px solid var(--color-border, #555);
    border-radius: 4px;
    color: var(--color-text-primary, #e8e8e8);
    font-size: 14px;
  }

  .search-input {
    flex: 1;
    padding: 10px 12px;
    background: var(--color-bg-primary, #2b2b2b);
    border: 1px solid var(--color-border, #555);
    border-radius: 4px;
    color: var(--color-text-primary, #e8e8e8);
    font-size: 14px;
  }

  .search-input:focus {
    outline: none;
    border-color: var(--color-accent, #5c7cfa);
  }

  .search-button {
    min-width: 100px;
  }

  .filter-row {
    display: flex;
    gap: 16px;
    align-items: center;
  }

  .filter-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: var(--color-text-secondary, #aaa);
  }

  .filter-label select {
    padding: 6px 10px;
    background: var(--color-bg-primary, #2b2b2b);
    border: 1px solid var(--color-border, #555);
    border-radius: 4px;
    color: var(--color-text-primary, #e8e8e8);
    font-size: 13px;
  }

  /* Results */
  .results-container {
    min-height: 200px;
  }

  .results-info {
    margin-bottom: 8px;
  }

  .results-count {
    font-size: 13px;
    color: var(--color-text-secondary, #aaa);
  }

  .loading-state,
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 200px;
    color: var(--color-text-secondary, #aaa);
  }

  .spinner {
    width: 24px;
    height: 24px;
    border: 2px solid var(--color-border, #555);
    border-top-color: var(--color-accent, #5c7cfa);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin-bottom: 12px;
  }

  .hint {
    font-size: 13px;
    color: var(--color-text-muted, #888);
    margin-top: 8px;
  }

  /* Pagination */
  .pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
    padding: 16px 0 8px;
    border-top: 1px solid var(--color-border, #555);
    margin-top: 8px;
  }

  .page-info {
    font-size: 13px;
    color: var(--color-text-secondary, #aaa);
  }

  /* Buttons */
  .btn {
    padding: 10px 16px;
    border-radius: 4px;
    font-size: 14px;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .btn-small {
    padding: 6px 12px;
    font-size: 13px;
  }

  .btn-primary {
    background: var(--color-accent, #5c7cfa);
    border: 1px solid var(--color-accent, #5c7cfa);
    color: white;
  }

  .btn-primary:hover:not(:disabled) {
    background: var(--color-accent-hover, #4c6ef5);
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  /* Responsive */
  @media (max-width: 768px) {
    .search-row {
      flex-wrap: wrap;
    }

    .search-index {
      width: 100%;
    }

    .search-input {
      width: 100%;
    }

    .search-button {
      width: 100%;
    }

    .source-tabs {
      flex-wrap: wrap;
    }
  }
</style>
